<template>
  <section class="dashboard">
    <!-- Grid: 4 Karten (responsive) -->
    <div class="dashboard-grid">
      <!-- 1) Meldungen -->
      <div
        ref="notificationsCardEl"
        class="card card-fixed"
      >
        <header class="card-header">
          <h2 class="h2">
            Meldungen
            <span
              class="text-sm font-normal muted"
              title="neue | akzeptierte"
            >({{ newNotifications.length }} | {{
              acceptedNotifications.length
            }})</span>
          </h2>
        </header>

        <div
          class="card-body"
          @scroll.passive="onNotificationsScroll"
        >
          <div
            v-if="showNotificationsSkeleton"
            class="skeleton"
          >
            <div class="line" />
            <div class="line" />
            <div class="line short" />
          </div>

          <template v-else>
            <div>
              <p class="meta mb-2 mt-0">
                neue Meldungen (offen/gelesen)
              </p>
              <ul class="list">
                <li
                  v-for="n in newNotifications"
                  :key="`new-${n.id}`"
                  :class="['clickable', isNotificationTodayOrFromToday(n) ? 'today' : '']"
                  role="button"
                  tabindex="0"
                  :title="n.subject ? `Meldung öffnen: ${truncate(n.subject, 50)}` : 'Meldung öffnen'"
                  @click="openNotification(n)"
                  @keydown.enter="openNotification(n)"
                  @keydown.space.prevent="openNotification(n)"
                >
                  <span class="item-title">{{ n.student_name || n.studentName }}</span>
                  <span
                    v-if="n.subject"
                    class="meta"
                    :title="n.subject"
                  > • {{ truncate(n.subject, 50) }}</span>
                  <div
                    v-if="formatNotificationPeriod(n)"
                    class="meta"
                  >
                    • {{ formatNotificationPeriod(n) }}
                  </div>
                </li>
                <li
                  v-if="newNotifications.length === 0"
                  class="muted text-small"
                >
                  keine neuen Meldungen
                </li>
              </ul>
            </div>

            <div class="mt-12">
              <p class="meta mb-2">
                akzeptiert
              </p>
              <ul class="list">
                <li
                  v-for="n in acceptedNotifications"
                  :key="`acc-${n.id}`"
                  class="released clickable"
                  role="button"
                  tabindex="0"
                  :title="n.subject ? `Meldung öffnen: ${truncate(n.subject, 50)}` : 'Meldung öffnen'"
                  @click="openNotification(n)"
                  @keydown.enter="openNotification(n)"
                  @keydown.space.prevent="openNotification(n)"
                >
                  <span class="item-title">{{ n.student_name || n.studentName }}</span>
                  <span
                    v-if="n.subject"
                    class="meta"
                    :title="n.subject"
                  > • {{ truncate(n.subject, 50) }}</span>
                  <div
                    v-if="formatNotificationPeriod(n)"
                    class="meta"
                  >
                    • {{ formatNotificationPeriod(n) }}
                  </div>
                </li>
                <li
                  v-if="acceptedNotifications.length === 0"
                  class="muted text-small"
                >
                  keine akzeptierten Meldungen
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
      <!-- Neu: Ungelesene Hinweise -->
      <div
        ref="hintsCardEl"
        class="card card-fixed"
      >
        <header class="card-header">
          <h2 class="h2">
            ungelesene Hinweise
            <span
              class="text-sm font-normal muted"
              title="ungelesene"
            >({{ unreadHints.length }})</span>
          </h2>
        </header>
        <div
          class="card-body"
          @scroll.passive="onHintsScroll"
        >
          <div
            v-if="showHintsSkeleton"
            class="skeleton"
          >
            <div class="line" />
            <div class="line" />
            <div class="line short" />
          </div>

          <ul
            v-else
            class="list"
          >
            <li
              v-for="h in unreadHints"
              :key="`hint-${h.id || h.uuid || (h.student_name || h.studentName || '') + '-' + (h.sent_at || h.created_at || h.createdAt || '')}`"
              :class="['clickable', isHintValidToday(h) ? 'today' : '']"
              role="button"
              tabindex="0"
              :title="`Hinweis öffnen: ${(h.subject || h.message || h.text || '').slice(0, 50)}`"
              @click="openHint(h)"
              @keydown.enter="openHint(h)"
              @keydown.space.prevent="openHint(h)"
            >
              <span class="item-title">{{
                h.student_name || h.studentName || (h.student && h.student.name) || 'Schüler'
              }}</span>
              <span
                v-if="h.subject || h.message || h.text"
                class="meta"
              > • {{ truncate(h.subject || h.message || h.text, 10) }}</span>
            </li>
            <li
              v-if="unreadHints.length === 0"
              class="muted text-small"
            >
              keine ungelesenen Hinweise
            </li>
          </ul>
        </div>
      </div>
      <!-- 2) Entlassene (nicht regulär) -->
      <div
        ref="releasedCardEl"
        class="card card-fixed"
      >
        <header class="card-header">
          <template v-if="!releasedFilterOpen">
            <div class="header-row">
              <h2
                class="h2 clickable"
                role="button"
                tabindex="0"
                title="Nach Schülernamen filtern"
                @click="releasedFilterOpen = true"
                @keydown.enter="releasedFilterOpen = true"
                @keydown.space.prevent="releasedFilterOpen = true"
              >
                entlassene Schüler
                <span class="text-sm font-normal muted">({{ releasedNonRegularFiltered.length }})</span>
              </h2>
              <button
                class="btn icon-btn"
                type="button"
                title="Filter öffnen"
                aria-label="Filter öffnen"
                @click="releasedFilterOpen = true"
              >
                <i
                  class="pi pi-search"
                  aria-hidden="true"
                />
              </button>
            </div>
          </template>
          <div
            v-else
            class="filter-bar"
          >
            <input
              v-model.trim="releasedFilterQuery"
              class="input filter-input"
              type="text"
              placeholder="Schülername filtern"
              autocomplete="off"
              aria-label="Schülername filtern"
            >
            <div class="actions">
              <button
                class="btn icon-btn"
                type="button"
                title="Filter leeren"
                aria-label="Filter leeren"
                @click="releasedFilterQuery = ''"
              >
                <i
                  class="pi pi-eraser"
                  aria-hidden="true"
                />
              </button>
              <button
                class="btn icon-btn"
                type="button"
                title="Filter schließen"
                aria-label="Filter schließen"
                @click="releasedFilterOpen = false; releasedFilterQuery = ''"
              >
                <i
                  class="pi pi-times"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </header>

        <div
          class="card-body"
          @scroll.passive="onReleasedScroll"
        >
          <div
            v-if="showReleasedSkeleton"
            class="skeleton"
          >
            <div class="line" />
            <div class="line" />
            <div class="line short" />
          </div>

          <ul
            v-else
            class="list"
          >
            <li
              v-for="r in releasedNonRegularFiltered"
              :key="`rel-${r.student_id}`"
              :class="[
                'clickable',
                hasReleaseMandatoryAlert(r) ? 'release-mandatory' : '',
                r.reason !== 'Schulschluss' ? 'released' : ''
              ]"
              role="button"
              tabindex="0"
              :title="`In Angebote öffnen: ${r.student_name}`"
              @click="openStudentInOffers(r.student_name)"
              @keydown.enter="openStudentInOffers(r.student_name)"
              @keydown.space.prevent="openStudentInOffers(r.student_name)"
            >
              <span class="item-title">{{ r.student_name }}</span>
              <span class="meta"> • {{ r.reason || 'ohne Grund' }} – {{ formatTime(r.checkout_at || r.out_at) }}</span>
            </li>
            <li
              v-if="releasedNonRegular.length === 0"
              class="muted text-small"
            >
              Niemand entlassen
            </li>
          </ul>
        </div>
      </div>

      <!-- 3) Abwesend ohne Meldung -->
      <div
        ref="absentCardEl"
        class="card card-fixed"
      >
        <header class="card-header">
          <template v-if="!absentFilterOpen">
            <div class="header-row">
              <h2
                class="h2 clickable"
                role="button"
                tabindex="0"
                title="Nach Schülernamen filtern"
                @click="absentFilterOpen = true"
                @keydown.enter="absentFilterOpen = true"
                @keydown.space.prevent="absentFilterOpen = true"
              >
                Abwesende
                <span class="text-sm font-normal muted">(o. Meldung: {{
                  absentWithoutNotificationFiltered.length
                }})</span>
              </h2>
              <button
                class="btn icon-btn"
                type="button"
                title="Filter öffnen"
                aria-label="Filter öffnen"
                @click="absentFilterOpen = true"
              >
                <i
                  class="pi pi-search"
                  aria-hidden="true"
                />
              </button>
            </div>
          </template>
          <div
            v-else
            class="filter-bar"
          >
            <input
              v-model.trim="absentFilterQuery"
              class="input filter-input"
              type="text"
              placeholder="Schülername filtern"
              autocomplete="off"
              aria-label="Schülername filtern"
            >
            <div class="actions">
              <button
                class="btn icon-btn"
                type="button"
                title="Filter leeren"
                aria-label="Filter leeren"
                @click="absentFilterQuery = ''"
              >
                <i
                  class="pi pi-eraser"
                  aria-hidden="true"
                />
              </button>
              <button
                class="btn icon-btn"
                type="button"
                title="Filter schließen"
                aria-label="Filter schließen"
                @click="absentFilterOpen = false; absentFilterQuery = ''"
              >
                <i
                  class="pi pi-times"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </header>

        <div
          class="card-body"
          @scroll.passive="onAbsentScroll"
        >
          <div
            v-if="showAbsentSkeleton"
            class="skeleton"
          >
            <div class="line" />
            <div class="line" />
            <div class="line short" />
          </div>

          <ul
            v-else
            class="list absent-list"
          >
            <li
              v-for="s in absentWithoutNotificationFiltered"
              :key="`abs-${s.student_id}`"
              class="clickable"
            >
              <span
                class="item-title"
                role="button"
                tabindex="0"
                :title="`In Angebote öffnen: ${s.student_name}`"
                @click="openStudentInOffers(s.student_name)"
                @keydown.enter="openStudentInOffers(s.student_name)"
                @keydown.space.prevent="openStudentInOffers(s.student_name)"
              >{{ s.student_name }}</span>

              <div class="actions p-0 m-0">
                <!-- Neu: Ankunft erfassen -->
                <button
                  class="btn icon-btn"
                  type="button"
                  title="Ankunft erfassen"
                  aria-label="Ankunft erfassen"
                  style="background: rgba(16, 185, 129, .1);"
                  @click.stop="openArriveDialogFromAbsent(s)"
                >
                  <i
                    class="pi pi-sign-in"
                    aria-hidden="true"
                    style="color: var(--success);"
                  />
                </button>

                <!-- Bestehender Mail-Button -->
                <button
                  v-if="Array.isArray(s.parents) && s.parents.length !== 0"
                  class="btn icon-btn"
                  type="button"
                  :title="hasAbsenceEmailSent(s) ? (`E-Mail gesendet${s.absence_email_sent_at ? ' — ' + formatTime(s.absence_email_sent_at) : ''}`) : 'E-Mail an Eltern senden'"
                  :style="hasAbsenceEmailSent(s) ? '':'background: rgba(245, 158, 11, .14);' "
                  :disabled="hasAbsenceEmailSent(s)"
                  @click.stop="openAbsenceMailDialog(s)"
                >
                  <i
                    class="pi pi-envelope"
                    aria-hidden="true"
                    style="position: relative;color: #aaa;"
                  >
                    <i
                      v-if="hasAbsenceEmailSent(s)"
                      class="pi pi-check"
                      aria-hidden="true"
                      style="position: absolute;right: -4px;font-size: 14px;bottom: 0;font-weight: bold;color: green;"
                    />
                  </i>
                </button>
              </div>
            </li>
            <li
              v-if="absentWithoutNotification.length === 0"
              class="muted text-small"
            >
              Keine abwesenden Schüler
            </li>
          </ul>
        </div>
      </div>

      <!-- 4) Noch in der Schule -->
      <div
        ref="presentCardEl"
        class="card card-fixed"
      >
        <header class="card-header">
          <template v-if="!presentFilterOpen">
            <div class="header-row">
              <h2
                class="h2 clickable"
                role="button"
                tabindex="0"
                title="Nach Schülernamen filtern"
                @click="presentFilterOpen = true"
                @keydown.enter="presentFilterOpen = true"
                @keydown.space.prevent="presentFilterOpen = true"
              >
                noch in der Schule
                <span class="text-sm font-normal muted">({{ presentStillInSchoolFiltered.length }})</span>
              </h2>
              <button
                class="btn icon-btn"
                type="button"
                title="Filter öffnen"
                aria-label="Filter öffnen"
                @click="presentFilterOpen = true"
              >
                <i
                  class="pi pi-search"
                  aria-hidden="true"
                />
              </button>
            </div>
          </template>
          <div
            v-else
            class="filter-bar"
          >
            <input
              v-model.trim="presentFilterQuery"
              class="input filter-input"
              type="text"
              placeholder="Schülername filtern"
              autocomplete="off"
              aria-label="Schülername filtern"
            >
            <div class="actions">
              <button
                class="btn icon-btn"
                type="button"
                title="Filter leeren"
                aria-label="Filter leeren"
                @click="presentFilterQuery = ''"
              >
                <i
                  class="pi pi-eraser"
                  aria-hidden="true"
                />
              </button>
              <button
                class="btn icon-btn"
                type="button"
                title="Filter schließen"
                aria-label="Filter schließen"
                @click="presentFilterOpen = false; presentFilterQuery = ''"
              >
                <i
                  class="pi pi-times"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </header>

        <div
          class="card-body"
          @scroll.passive="onPresentScroll"
        >
          <div
            v-if="showPresentSkeleton"
            class="skeleton"
          >
            <div class="line" />
            <div class="line" />
            <div class="line short" />
          </div>

          <ul
            v-else
            class="list present-list"
          >
            <li
              v-for="s in presentStillInSchoolFiltered"
              :key="`in-${s.student_id}`"
              class="clickable"
              role="button"
              tabindex="0"
              :title="`In Angebote öffnen: ${s.student_name}`"
              @click="openStudentInOffers(s.student_name)"
              @keydown.enter="openStudentInOffers(s.student_name)"
              @keydown.space.prevent="openStudentInOffers(s.student_name)"
            >
              <div>
                <span class="item-title">{{ s.student_name }}</span>
                <span class="meta">
                  • seit {{ formatTime(s.first_checkin_at || s.checkin_at || s.in_at) }}
                </span>
              </div>

              <div class="actions p-0 m-0">
                <button
                  class="btn icon-btn"
                  type="button"
                  title="Entlassen"
                  style="background: rgba(245, 158, 11, .14);"
                  @click.stop="openReleaseDialog(s)"
                >
                  <i
                    class="pi pi-sign-out"
                    aria-hidden="true"
                    style="color: var(--warn);"
                  />
                </button>
              </div>
            </li>
            <li
              v-if="presentStillInSchool.length === 0"
              class="muted text-small"
            >
              Niemand mehr in der Schule
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Schülersuche + Geburtstage in einer Zeile -->
    <div class="mt-12 search-birthdays-grid">
      <!-- Schülersuche (1/2 Breite) -->
      <section class="card student-search-card">
        <h2 class="h2">
          Schülersuche
        </h2>
        <div class="student-search actions">
          <div class="student-search__field">
            <span
              class="student-search__icon"
              aria-hidden="true"
            />
            <input
              id="student-search"
              v-model.trim="searchQuery"
              class="input student-search__input"
              type="text"
              placeholder="Schülername eingeben"
              list="students-datalist"
              autocomplete="off"
              aria-label="Schülername eingeben und mit Enter suchen"
              @keydown.enter.prevent="doSearch"
            >
            <datalist id="students-datalist">
              <option
                v-for="s in suggestions"
                :key="s.id ? `sug-${s.id}` : `sug-${s}`"
                :value="s.name || s"
              />
            </datalist>
          </div>

          <button
            class="btn btn-primary"
            type="button"
            :disabled="searchLoading || !searchQuery"
            @click="doSearch"
          >
            {{ searchLoading ? 'Suche…' : 'Suchen' }}
          </button>
        </div>

        <div class="mt-3">
          <p
            v-if="searchError"
            class="notice notice-danger"
            role="alert"
          >
            {{ searchError }}
          </p>

          <template v-else-if="searchPerformed">
            <ul class="list">
              <li v-if="searchResult.present">
                <span class="item-title">Anwesend</span>
                <span class="meta"> • {{ searchResult.present.offerName }} — {{
                  searchResult.present.teacherName
                }}</span>
              </li>
              <li
                v-if="searchResult.released"
                class="released"
              >
                <span class="item-title">Entlassen</span>
                <span class="meta"> • {{
                  formatTime(searchResult.released.at)
                }} — {{ searchResult.released.reason || 'ohne Grund' }}</span>
              </li>
              <li
                v-if="searchResult.notification"
                class="clickable released"
                role="button"
                tabindex="0"
                :title="searchResult.notification.subject ? `Meldung öffnen: ${truncate(searchResult.notification.subject, 50)}` : 'Meldung öffnen'"
                @click="openNotification(searchResult.notification)"
                @keydown.enter="openNotification(searchResult.notification)"
                @keydown.space.prevent="openNotification(searchResult.notification)"
              >
                <span class="item-title">Meldung</span>
                <span
                  v-if="searchResult.notification.subject"
                  class="meta"
                > • {{ truncate(searchResult.notification.subject, 80) }}</span>
              </li>
              <li
                v-if="!searchResult.present && !searchResult.released && !searchResult.notification"
                class="muted text-small"
              >
                keine Informationen gefunden
              </li>
            </ul>
          </template>
        </div>
      </section>

      <!-- Heute Geburtstag (1/4 Breite) -->
      <div class="card birthday-card">
        <header class="card-header">
          <h2 class="h2">
            Heute Geburtstag
          </h2>
        </header>
        <div class="card-body">
          <div
            v-if="birthdaysLoading"
            class="skeleton"
          >
            <div class="line" />
            <div class="line short" />
          </div>
          <ul
            v-else
            class="list"
          >
            <li
              v-for="s in birthdaysToday"
              :key="`bday-today-${s.id || s.name}`"
            >
              <span class="item-title">{{ s.name }}</span>
            </li>
            <li
              v-if="birthdaysToday.length === 0"
              class="muted text-small"
            >
              keine Geburtstage heute
            </li>
          </ul>
        </div>
      </div>

      <!-- In den nächsten 7 Tagen (1/4 Breite) -->
      <div class="card birthday-card">
        <header class="card-header">
          <h2 class="h2">
            In den nächsten 7 Tagen
          </h2>
        </header>
        <div class="card-body">
          <div
            v-if="birthdaysLoading"
            class="skeleton"
          >
            <div class="line" />
            <div class="line short" />
          </div>
          <ul
            v-else
            class="list"
          >
            <li
              v-for="s in birthdaysUpcoming"
              :key="`bday-up-${s.id || (s.name + (s.date || ''))}`"
            >
              <span class="item-title">{{ s.name }}</span>
              <span
                v-if="s.date"
                class="meta"
              > • {{ formatDateShort(s.date) }}</span>
            </li>
            <li
              v-if="birthdaysUpcoming.length === 0"
              class="muted text-small"
            >
              keine anstehenden Geburtstage
            </li>
          </ul>
        </div>
      </div>
    </div>

    <HintViewDialog
      :open="!!selectedHint"
      :hint="selectedHint || {}"
      @close="selectedHint = null"
      @marked-read="selectedHint = null; loadUnreadHints()"
    />

    <NotificationViewDialog
      :open="!!selectedNotification"
      :notification="selectedNotification || {}"
      :loading="false"
      @close="selectedNotification = null; load()"
      @accept="onNotificationUpdated"
      @reject="onNotificationUpdated"
      @read="onNotificationRead"
      @create-correction="onCreateCorrectionFromDialog"
    />
    <NotificationCreateDialog
      :open="correctionOpen"
      :notif-parent-id="correctionSource?.id ?? null"
      :prefill-student-id="nStudentId(correctionSource)"
      :prefill-subject="nSubject(correctionSource)"
      :prefill-date-from="nDateFrom(correctionSource)"
      :prefill-date-to="nDateTo(correctionSource)"
      :prefill-time-from="nTimeFrom(correctionSource)"
      :prefill-time-to="nTimeTo(correctionSource)"
      :prefill-kind="nKind(correctionSource)"
      @close="correctionOpen = false; correctionSource = null"
      @created="onCorrectionCreated"
    />

    <MessageSendDialog
      :open="emailDialogOpen"
      :topic="'absence_email'"
      :student-id="emailDialogData?.studentId || 0"
      :recipients-options="emailDialogData?.recipients || []"
      :preselected-recipient-user-ids="emailDialogData?.preselectedRecipientUserIds || []"
      :default-subject="emailDialogData?.subject || ''"
      :default-message="emailDialogData?.message || ''"
      @close="onEmailDialogClosed"
      @sent="onEmailSent"
    />

    <StudentReleaseConfirmDialog
      :open="releaseConfirmOpen"
      :student="releaseStudent"
      :occurred-at="releaseAtIso"
      @close="closeReleaseDialog"
      @released="load"
    />

    <StudentArriveConfirmDialog
      :open="arriveConfirmOpen"
      :student-name="arriveStudentName"
      @close="closeArriveDialog"
      @confirm="confirmArrive"
    />
  </section>
</template>

<script setup>
import {onMounted, onUnmounted, ref, computed, watch} from 'vue';
import {useRouter} from 'vue-router';
import NotificationViewDialog from '@/components/notifications/NotificationViewDialog.vue';
import NotificationCreateDialog from '@/components/notifications/NotificationCreateDialog.vue';
import HintViewDialog from '@/components/hints/HintViewDialog.vue';
import MessageSendDialog from '@/components/communications/MessageSendDialog.vue';
import {hintsApi} from '@/services/api/hints';
import {adminApi} from '@/services/api/admin';
import {studentApi} from '@/services/api/student';
import {presenceApi} from '@/services/api/presence';
import {useAbsenceEmailController} from '@/controllers/useAbsenceEmailController';
import {useToast} from 'primevue/usetoast';
import StudentArriveConfirmDialog from '@/components/dialogs/StudentArriveConfirmDialog.vue';
import StudentReleaseConfirmDialog from '@/components/dialogs/StudentReleaseConfirmDialog.vue';

const loading = ref(true);
const overview = ref({});
const dashboard = ref({
  notifications: {accepted: [], new: []},
  releasedNonRegular: [],
  absentWithoutNotification: [],
  presentStillInSchool: []
});

const notificationsCardEl = ref(null);
const hintsCardEl = ref(null);
const releasedCardEl = ref(null);
const absentCardEl = ref(null);
const presentCardEl = ref(null);

const notificationsWasScrolled = ref(false);
const hintsWasScrolled = ref(false);
const releasedWasScrolled = ref(false);
const absentWasScrolled = ref(false);
const presentWasScrolled = ref(false);

function onNotificationsScroll(e) {
  if (notificationsWasScrolled.value) return;
  const el = e?.target;
  if (!el) return;

  if ((el.scrollTop || 0) > 0) {
    notificationsWasScrolled.value = true;
  }
}

function onHintsScroll(e) {
  if (hintsWasScrolled.value) return;
  const el = e?.target;
  if (!el) return;

  if ((el.scrollTop || 0) > 0) {
    hintsWasScrolled.value = true;
  }
}

function onReleasedScroll(e) {
  if (releasedWasScrolled.value) return;
  const el = e?.target;
  if (!el) return;

  if ((el.scrollTop || 0) > 0) {
    releasedWasScrolled.value = true;
  }
}

function onAbsentScroll(e) {
  if (absentWasScrolled.value) return;
  const el = e?.target;
  if (!el) return;

  if ((el.scrollTop || 0) > 0) {
    absentWasScrolled.value = true;
  }
}

function onPresentScroll(e) {
  if (presentWasScrolled.value) return;
  const el = e?.target;
  if (!el) return;

  if ((el.scrollTop || 0) > 0) {
    presentWasScrolled.value = true;
  }
}

const showNotificationsSkeleton = computed(() => {
  return loading.value && !notificationsWasScrolled.value;
});
const showHintsSkeleton = computed(() => {
  return hintsLoading.value && !hintsWasScrolled.value;
});
const showReleasedSkeleton = computed(() => {
  return loading.value && !releasedWasScrolled.value;
});
const showAbsentSkeleton = computed(() => {
  return loading.value && !absentWasScrolled.value;
});
const showPresentSkeleton = computed(() => {
  return loading.value && !presentWasScrolled.value;
});

function onGlobalPointerDown(e) {
  const t = e?.target;
  if (!t) return;

  const isIn = (el) => !!el && el.contains(t);

  if (!isIn(notificationsCardEl.value)) notificationsWasScrolled.value = false;
  if (!isIn(hintsCardEl.value)) hintsWasScrolled.value = false;
  if (!isIn(releasedCardEl.value)) releasedWasScrolled.value = false;
  if (!isIn(absentCardEl.value)) absentWasScrolled.value = false;
  if (!isIn(presentCardEl.value)) presentWasScrolled.value = false;
}

const todayYmd = new Date().toISOString().slice(0, 10);
let refreshTimer = null;

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'});
  } catch {
    return '';
  }
}

function hasReleaseMandatoryAlert(r) {
  return r?.hasReleaseMandatoryAlert === true;
}


// Prüft robust, ob der Schüler heute ein Angebot gebucht hat
function hasBookedOfferToday(s) {
  const today = todayYmd;

  // Explizite Bool-Flags
  const flagKeys = ['hasOfferToday', 'has_offer_today', 'offerBooked', 'offer_booked', 'hasOffer', 'has_offer', 'booked', 'booked_today'];
  for (const k of flagKeys) {
    if (s && s[k] === true) return true;
  }

  // Arrays möglicher Buchungen
  const arrays = [];
  if (Array.isArray(s?.offersToday)) arrays.push(...s.offersToday);
  if (Array.isArray(s?.offers)) arrays.push(...s.offers);
  if (Array.isArray(s?.bookings)) arrays.push(...s.bookings);
  if (Array.isArray(s?.bookings_today)) arrays.push(...s.bookings_today);

  const arrayHasToday = arrays.some(it => {
    const date = it?.date ?? it?.for_date ?? it?.day ?? it?.start_date ?? it?.date_from ?? null;
    if (date) return String(date).slice(0, 10) === today;
    // Ohne Datum gehen wir auf Nummer sicher und werten NICHT als heute
    return false;
  });
  if (arrayHasToday) return true;

  // Einzel-Referenz + Datum
  const hasOfferRef = s?.offer_id != null || s?.offerId != null || (s?.offer && (s.offer.id != null || s.offer.name));
  const singleDate = s?.date ?? s?.for_date ?? s?.booking_date ?? s?.offer_date ?? s?.day ?? null;
  if (hasOfferRef && singleDate && String(singleDate).slice(0, 10) === today) return true;

  return false;
}

const acceptedNotifications = computed(() => dashboard.value?.notifications?.accepted ?? []);
const newNotifications = computed(() => dashboard.value?.notifications?.new ?? []);
const releasedNonRegular = computed(() => dashboard.value?.releasedNonRegular ?? []);
const absentWithoutNotificationRaw = computed(() => dashboard.value?.absentWithoutNotification ?? []);
const absentWithoutNotification = computed(() => {
  const list = Array.isArray(absentWithoutNotificationRaw.value) ? absentWithoutNotificationRaw.value : [];
  return list.filter(s => hasBookedOfferToday(s));
});
const presentStillInSchool = computed(() => dashboard.value?.presentStillInSchool ?? []);

// Hilfsfunktion: Phrasen-Match auf Schülernamen
function nameMatchesFilter(name, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;

  const n = String(name || '').toLowerCase();
  if (!n) return false;

  const words = n.split(/[\s,.-]+/).filter(Boolean);
  if (words.length === 0) return false;

  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;

  return tokens.every(tok => words.some(w => w.includes(tok)));
}


// Filter-UI States
const absentFilterOpen = ref(false);
const absentFilterQuery = ref('');
const presentFilterOpen = ref(false);
const presentFilterQuery = ref('');
const releasedFilterOpen = ref(false);
const releasedFilterQuery = ref('');

// Gefilterte Listen
const absentWithoutNotificationFiltered = computed(() => {
  const src = Array.isArray(absentWithoutNotification.value) ? absentWithoutNotification.value : [];
  if (!absentFilterQuery.value) return src;
  return src.filter(s => nameMatchesFilter(s?.student_name, absentFilterQuery.value));
});

const presentStillInSchoolFiltered = computed(() => {
  const src = Array.isArray(presentStillInSchool.value) ? presentStillInSchool.value : [];
  if (!presentFilterQuery.value) return src;
  return src.filter(s => nameMatchesFilter(s?.student_name, presentFilterQuery.value));
});

const releasedNonRegularFiltered = computed(() => {
  const src = Array.isArray(releasedNonRegular.value) ? releasedNonRegular.value : [];
  const q = String(releasedFilterQuery.value || '').trim();

  const filtered = !q ? src : src.filter(r => nameMatchesFilter(r?.student_name, q));

  // Alerts nach oben pinnen (stable-ish: erst pin, dann Name)
  return [...filtered].sort((a, b) => {
    const ap = hasReleaseMandatoryAlert(a) ? 1 : 0;
    const bp = hasReleaseMandatoryAlert(b) ? 1 : 0;
    if (ap !== bp) return bp - ap;

    const an = String(a?.student_name || '');
    const bn = String(b?.student_name || '');
    return an.localeCompare(bn, 'de-DE', {sensitivity: 'base'});
  });
});

const router = useRouter();
const selectedNotification = ref(null);
const correctionOpen = ref(false);
const correctionSource = ref(null);

// E-Mail Dialog (Abwesenheit) – über Controller
const {
  emailDialogOpen,
  emailDialogData,
  hasAbsenceEmailSent,
  openAbsenceMailDialog,
  handleEmailSent
} = useAbsenceEmailController();

// Toast via PrimeVue (wie in Groups.vue)
const toast = useToast();
const lastEmailActionWasSent = ref(false);

// Entlassungs-Dialog State
const releaseConfirmOpen = ref(false);
const releaseStudent = ref(null);
const releaseAtIso = ref('');

// Ankunfts-Dialog State
const arriveConfirmOpen = ref(false);
const arriveStudent = ref(null);
const arriveStudentName = ref('');

// Hilfsfunktion: Student-ID robust ermitteln
function getStudentId(s) {
  const id = s?.student_id ?? s?.id ?? null;
  const num = Number(id);
  return Number.isFinite(num) ? num : null;
}

function openReleaseDialog(s) {
  releaseStudent.value = s || null;
  releaseAtIso.value = new Date().toISOString();
  releaseConfirmOpen.value = true;
}

function closeReleaseDialog() {
  releaseConfirmOpen.value = false;
  releaseStudent.value = null;
  releaseAtIso.value = '';
}

// Neu: Abwesende → Ankunft erfassen
function openArriveDialogFromAbsent(s) {
  arriveStudent.value = s || null;
  arriveStudentName.value = s?.student_name || '';
  arriveConfirmOpen.value = true;
}

function closeArriveDialog() {
  arriveConfirmOpen.value = false;
  arriveStudent.value = null;
  arriveStudentName.value = '';
}

async function confirmArrive(payload) {
  if (!arriveStudent.value) return;
  const id = getStudentId(arriveStudent.value);
  if (!id) {
    toast.add({severity: 'error', summary: 'Ungültige Schüler-ID.', life: 3000});
    return;
  }

  const when = payload?.occurredAt || new Date().toISOString();

  try {
    await presenceApi.in({
      studentId: id,
      occurredAt: when
    });
    toast.add({
      severity: 'success',
      summary: `${arriveStudentName.value || 'Schüler'} ist angekommen.`,
      life: 3000
    });
    closeArriveDialog();
    load();
  } catch (e) {
    const msg = e?.response?.data?.message || 'Ankunft konnte nicht gespeichert werden.';
    toast.add({severity: 'error', summary: msg, life: 4000});
  }
}

function onEmailSent() {
  lastEmailActionWasSent.value = true;
  handleEmailSent();
  toast.add({severity: 'success', summary: 'E-Mail erfolgreich gesendet.', life: 3500});
  load();
}

function onEmailDialogClosed() {
  // Dialog-State zurücksetzen (analog zu vorherigem Verhalten)
  emailDialogOpen.value = false;
  emailDialogData.value = null;

  if (!lastEmailActionWasSent.value) {
    toast.add({severity: 'info', summary: 'E-Mail-Versand nicht durchgeführt.', life: 3000});
  }
  lastEmailActionWasSent.value = false;
}

// Hinweise
const selectedHint = ref(null);
const hintsLoading = ref(false);
const unreadHints = ref([]);

function openNotification(n) {
  // Nur die ID übergeben – der Dialog lädt Details selbst nach
  selectedNotification.value = n && n.id ? {id: n.id} : null;
}

function onCreateCorrectionFromDialog(data) {
  // Viewer schließen und Korrekturdialog mit Prefills öffnen
  selectedNotification.value = null;
  correctionSource.value = data || {};
  correctionOpen.value = true;
}

function onCorrectionCreated() {
  correctionOpen.value = false;
  correctionSource.value = null;
  load();
}

// Prefill-Helfer
function nStudentId(n) {
  const id = n?.studentId ?? n?.student_id ?? null;
  const num = Number(id);
  return Number.isFinite(num) ? num : null;
}

function nKind(n) {
  const tF = n?.timeFrom ?? n?.time_from ?? null;
  const tT = n?.timeTo ?? n?.time_to ?? null;
  return (tF || tT) ? 'time' : 'daily';
}

function nDateFrom(n) {
  return n?.dateFrom ?? n?.date_from ?? null;
}

function nDateTo(n) {
  return nKind(n) === 'time'
      ? (nDateFrom(n) ?? null)
      : (n?.dateTo ?? n?.date_to ?? null);
}

function nTimeFrom(n) {
  const v = n?.timeFrom ?? n?.time_from ?? null;
  if (!v) return null;
  if (typeof v === 'string') {
    const m = v.match(/T(\d{2}):(\d{2})/);
    if (m) return `${m[1]}:${m[2]}`;
    const m2 = v.match(/^(\d{2}):(\d{2})/);
    if (m2) return `${m2[1]}:${m2[2]}`;
  }
  return v;
}

function nTimeTo(n) {
  const v = n?.timeTo ?? n?.time_to ?? null;
  if (!v) return null;
  if (typeof v === 'string') {
    const m = v.match(/T(\d{2}):(\d{2})/);
    if (m) return `${m[1]}:${m[2]}`;
    const m2 = v.match(/^(\d{2}):(\d{2})/);
    if (m2) return `${m2[1]}:${m2[2]}`;
  }
  return v;
}

function nSubject(n) {
  const s = String(n?.subject ?? n?.subject_text ?? '').trim();
  return s ? `Korrektur zu: ${s}` : 'Korrekturmeldung';
}

function onNotificationUpdated() {
  selectedNotification.value = null;
  load();
}

function onNotificationRead() {
  // Beim automatischen "als gelesen" nur Daten neu laden, Dialog geöffnet lassen
  load();
}

function openStudentInOffers(studentName) {
  const q = String(studentName || '').trim();
  const routeData = router.resolve({
    path: '/offers-day-view',
    query: {group: 'student', search: q}
  });
  window.open(routeData.href, '_blank', 'noopener');
}

/* ---- Schülersuche ---- */
const searchQuery = ref('');
const suggestions = ref([]);
const searchLoading = ref(false);
const searchPerformed = ref(false);
const searchError = ref('');
const searchResult = ref({
  present: null,
  released: null,
  notification: null
});
let suggestTimer = null;

async function fetchSuggestions(q) {
  try {
    const res = await studentApi.searchStudents({q});
    const raw = res?.data?.data ?? res?.data ?? [];
    // Unterstützt sowohl Array von Strings als auch Objekte mit {id,name}
    suggestions.value = (Array.isArray(raw) ? raw : []).map(it => {
      if (typeof it === 'string') return it;
      const name = it?.name || it?.student_name || it?.fullName || '';
      return {id: it?.id ?? it?.student_id ?? name, name};
    }).filter(s => (typeof s === 'string' ? s.trim().length : String(s.name || '').trim().length));
  } catch {
    // stiller Fehler -> keine Vorschläge
    suggestions.value = [];
  }
}

watch(searchQuery, (q) => {
  if (suggestTimer) clearTimeout(suggestTimer);
  const s = String(q || '').trim();
  if (s.length < 2) {
    suggestions.value = [];
    return;
  }
  suggestTimer = setTimeout(() => fetchSuggestions(s), 250);
});

function normalizeStudentStatusPayload(raw) {
  const d = raw?.data?.data ?? raw?.data ?? raw ?? {};

  const presentSrc = d.present ?? d.attendance ?? d.current ?? d.currentAttendance ?? null;
  const present = presentSrc ? {
    offerName: presentSrc.offer?.name ?? presentSrc.offer_name ?? presentSrc.offerName ?? presentSrc.subject ?? 'Unbekanntes Angebot',
    teacherName: presentSrc.teacher?.name ?? presentSrc.teacher_name ?? presentSrc.teacherName ?? presentSrc.teacher ?? 'Unbekannt'
  } : null;

  const relSrc = d.released ?? d.release ?? d.lastRelease ?? d.checkout ?? null;
  const released = relSrc ? {
    reason: relSrc.reason ?? relSrc.note ?? relSrc.message ?? '',
    at: relSrc.at ?? relSrc.time ?? relSrc.checkout_at ?? relSrc.out_at ?? null
  } : null;

  let notifObj = d.notification ?? d.lastNotification ?? null;
  if (!notifObj && Array.isArray(d.notifications) && d.notifications.length) {
    notifObj = d.notifications[0];
  }
  const notification = notifObj ? {
    id: notifObj.id ?? null,
    subject: notifObj.subject ?? ''
  } : null;

  return {present, released, notification};
}

async function doSearch() {
  const q = String(searchQuery.value || '').trim();
  if (!q) return;
  searchLoading.value = true;
  searchError.value = '';
  searchPerformed.value = false;
  searchResult.value = {present: null, released: null, notification: null};
  try {
    const res = await studentApi.getStudentStatus({name: q});
    searchResult.value = normalizeStudentStatusPayload(res);
    searchPerformed.value = true;
  } catch (e) {
    searchError.value = e?.response?.data?.message || 'Suche fehlgeschlagen.';
  } finally {
    searchLoading.value = false;
  }
}

/* ---- Ende Schülersuche ---- */

// Hinweise: laden/öffnen
function isHintValidToday(h) {
  const v = h?.validOn ?? h?.valid_on ?? h?.for_date ?? h?.date ?? null;
  if (!v) return false;
  const ymd = String(v).slice(0, 10);
  return ymd === todayYmd;
}

// Neue Meldungen: heute oder ab heute markieren
function isNotificationTodayOrFromToday(n) {
  const df = n?.dateFrom ?? n?.date_from ?? null;
  const dtRaw = n?.dateTo ?? n?.date_to ?? null;
  if (!df) return false;

  const start = String(df).slice(0, 10);
  const end = String(dtRaw || df).slice(0, 10);

  return start <= todayYmd && end >= todayYmd;
}

async function loadUnreadHints() {
  hintsLoading.value = true;
  try {
    const res = await hintsApi.getUnread({role: 'admin'});
    const raw = res?.data?.data ?? res?.data ?? [];
    unreadHints.value = Array.isArray(raw) ? raw : [];
  } catch {
    unreadHints.value = [];
  } finally {
    hintsLoading.value = false;
  }
}

function openHint(h) {
  selectedHint.value = h || null;
}

// Geburtstage (heute / nächste 7 Tage)
const birthdaysLoading = ref(false);
const birthdaysToday = ref([]);
const birthdaysUpcoming = ref([]);

function normalizeBirthdaysPayload(res) {
  const d = res?.data?.data ?? res?.data ?? res ?? {};
  const srcToday = Array.isArray(d.today) ? d.today
      : (Array.isArray(d.birthdays?.today) ? d.birthdays.today : []);
  const srcUpcoming = Array.isArray(d.upcoming) ? d.upcoming
      : (Array.isArray(d.birthdays?.upcoming) ? d.birthdays.upcoming
          : (Array.isArray(d.next7Days) ? d.next7Days
              : (Array.isArray(d.within7Days) ? d.within7Days : [])));

  const mapItem = (it) => {
    if (typeof it === 'string') return {id: it, name: it, date: null, active: true};
    const name = it?.name ?? it?.student_name ?? it?.fullName ?? it?.studentName ?? '';
    const date = it?.date ?? it?.birthday ?? it?.birth_date ?? it?.birthDate ?? null;
    const id = it?.id ?? it?.student_id ?? name;

    // Active-Flag robust ableiten
    let active = true;
    if (typeof it?.active === 'boolean') active = it.active;
    else if (typeof it?.is_active === 'boolean') active = it.is_active;
    else if (typeof it?.status === 'boolean') active = it.status;
    else if (typeof it?.status === 'string') {
      const s = it.status.toLowerCase();
      active = (s === 'active' || s === 'aktiv' || s === '1' || s === 'true');
    } else if (it?.status_id != null) {
      active = Number(it.status_id) === 1;
    } else if (it?.student_status_id != null) {
      active = Number(it.student_status_id) === 1;
    }

    return {id, name, date, active};
  };

  // nur aktive Schüler
  const todayList = srcToday.map(mapItem).filter(s => s.name && s.active !== false);

  const todayStr = todayYmd;

  const upcomingRaw = srcUpcoming.map(mapItem).filter(s => s.name && s.active !== false);
  const upcomingFiltered = upcomingRaw.filter(s => {
    if (!s.date) return true;
    const ymd = String(s.date).slice(0, 10);
    if (ymd === todayStr) return false;
    const dt = new Date(ymd);
    const t0 = new Date(todayStr);
    const diff = Math.round((dt - t0) / (1000 * 60 * 60 * 24));
    return diff >= 1 && diff <= 7;
  }).sort((a, b) => {
    const ta = a.date ? new Date(String(a.date).slice(0, 10)).getTime() : Infinity;
    const tb = b.date ? new Date(String(b.date).slice(0, 10)).getTime() : Infinity;
    return ta - tb;
  });

  return {todayList, upcomingList: upcomingFiltered};
}

function formatDateShort(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', {weekday: 'short', day: '2-digit', month: '2-digit'});
  } catch {
    return String(iso);
  }
}

async function loadBirthdays() {
  birthdaysLoading.value = true;
  try {
    const res = await adminApi.getBirthdays({date: todayYmd, daysAhead: 7, activeOnly: true});
    const {todayList, upcomingList} = normalizeBirthdaysPayload(res);
    birthdaysToday.value = todayList;
    birthdaysUpcoming.value = upcomingList;
  } catch {
    birthdaysToday.value = [];
    birthdaysUpcoming.value = [];
  } finally {
    birthdaysLoading.value = false;
  }
}

async function load() {
  loading.value = true;
  try {
    const res = await adminApi.getOverview({date: todayYmd});
    overview.value = res?.data || {};
    dashboard.value = res?.data?.dashboard || dashboard.value;
  } catch (e) {
    // stiller Fehler
  } finally {
    loading.value = false;
  }
}

// Kürzt Text nach max Zeichen und hängt '...' an (wenn nötig)
function truncate(txt, max = 120) {
  if (!txt && txt !== 0) return '';
  const s = String(txt);
  return s.length > max ? s.slice(0, max) + '...' : s;
}

// Datum als TT.MM.JJJJ formatiert (robust gegen ISO/Strings)
function formatDateDmy(iso) {
  if (!iso) return '';
  try {
    const ymd = String(iso).slice(0, 10);
    const [y, m, d] = ymd.split('-');
    if (y && m && d) return `${d}.${m}.${y}`;
    const dObj = new Date(iso);
    if (!Number.isNaN(dObj.getTime())) {
      return dObj.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
    }
    return String(iso);
  } catch {
    return String(iso);
  }
}

// Zeitraum einer Meldung als kompakte Zeichenkette
function formatNotificationPeriod(n) {
  if (!n) return '';
  // Wenn das Backend bereits einen Zeitraum-String liefert, diesen bevorzugen
  const backend = String(n?.period ?? n?.period_text ?? n?.periodText ?? '').trim();
  if (backend) return backend;

  const df = n?.dateFrom ?? n?.date_from ?? n?.date ?? n?.for_date ?? null;
  const dt = n?.dateTo ?? n?.date_to ?? null;
  const tF = nTimeFrom(n);
  const tT = nTimeTo(n);

  const datesDiffer = df && dt && String(df).slice(0, 10) !== String(dt).slice(0, 10);

  let timeStr = '';
  if (tF && tT) timeStr = `${tF}–${tT}`;
  else if (tF) timeStr = `ab ${tF}`;
  else if (tT) timeStr = `bis ${tT}`;

  if (datesDiffer) {
    return `${formatDateDmy(df)} – ${formatDateDmy(dt)}`;
  }
  if (df) {
    return timeStr ? `${formatDateDmy(df)}, ${timeStr}` : `${formatDateDmy(df)}`;
  }
  return timeStr;
}

onMounted(() => {
  window.addEventListener('pointerdown', onGlobalPointerDown, true);

  load();
  loadBirthdays();
  loadUnreadHints();
  // Auto-Refresh alle 30 Sekunden
  refreshTimer = setInterval(() => {
    load();
    loadBirthdays();
    loadUnreadHints();
  }, 30000);
});

onUnmounted(() => {
  window.removeEventListener('pointerdown', onGlobalPointerDown, true);

  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  if (suggestTimer) {
    clearTimeout(suggestTimer);
    suggestTimer = null;
  }
});
</script>

<style scoped>
.dashboard {
  display: block;
}

.dashboard-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr 1fr 1.1fr 1fr;
}


@media (max-width: 799px) {
  .dashboard {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .dashboard-grid {
    grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  }
}

/* Feste Kartenhöhe + Scrollbereich */
.card-fixed {
  display: flex;
  flex-direction: column;
  height: 420px;
  padding: 15px;
}

.card-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
  min-width: 0;
}

.card-body {
  flex: 1;
  min-height: 0; /* notwendig für overflow in flex children */
  overflow-y: auto;
  padding-right: 4px; /* Platz für Scrollbar */
}

.h2 {
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  margin: 0;
}

.meta {
  color: var(--muted);
  font-size: 12px;
  line-height: 16px;
}

/* Dezente, abgesetzte Listeneinträge */
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  line-height: 14px;
}

.list li {
  padding: 4px 6px;
  background: var(--bg);
  border-radius: 8px;
  border-left: 3px solid var(--border-strong);
}

.list li.clickable {
  cursor: pointer;
}

.list li.clickable:focus-visible {
  outline: 3px solid var(--focus);
  outline-offset: 2px;
}

.list li.released, .list li.today {
  background: rgba(245, 158, 11, .14);
  border-left-color: var(--warn);
}

/* Neu: Pflichtangebot-Alert nach Entlassung */
.list li.release-mandatory {
  background: rgba(239, 68, 68, 0.10);
  border-top: 1px solid;
  border-right: 1px solid;
  border-bottom: 1px solid;
  border-color: var(--danger);
}

.list li.released {
  background: rgba(245, 158, 11, .14);
  border-left-color: var(--warn);
}

.item-title {
  font-size: 14px;
  line-height: 20px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.actions .btn.icon-btn {
  height: 30px;
  width: 30px;
}

/* Skeleton Loader */
.skeleton .line {
  height: 14px;
  background: linear-gradient(90deg, var(--bg), #eceff4, var(--bg));
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 6px;
  margin: 8px 0;
}

.skeleton .line.short {
  width: 60%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Notices gemäß Style-Guide (lokal, kompakt) */
.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 20px;
  background: var(--surface);
}

.notice-danger {
  background: rgba(239, 68, 68, 0.08);
  border-color: #fecaca;
  color: #7f1d1d;
}

/* Schülersuche */
.student-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.student-search__field {
  position: relative;
  flex: 1 1 auto;
}

.student-search__icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  background-color: var(--muted);
  mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="%23000"><path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.32 3.34 10 3.34S4.01 6.01 4.01 9.5 6.68 15.66 10 15.66c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0s.41-1.08 0-1.49L15.5 14zm-5.5 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>') no-repeat 50% 50%;
  -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="%23000"><path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.32 3.34 10 3.34S4.01 6.01 4.01 9.5 6.68 15.66 10 15.66c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0s.41-1.08 0-1.49L15.5 14zm-5.5 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>') no-repeat 50% 50%;
  pointer-events: none;
}

.student-search__input {
  height: 38px;
  padding: 8px 8px 8px 36px;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  background: var(--surface-card);
  width: 100%;
}

.student-search__input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

@media (max-width: 480px) {
  .student-search {
    flex-direction: column;
    align-items: stretch;
  }

  .student-search__field {
    width: 100%;
  }

  .dashboard-grid {
    gap: 0;
  }
}

/* Schülersuche + Geburtstage: 3-Spalten-Grid (2fr/1fr/1fr), mobil einspaltig */
.search-birthdays-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: 2.05fr 1fr 1fr;
}

@media (max-width: 1199px) {
  .search-birthdays-grid {
    grid-template-columns: 1fr;
  }
}

/* Feste Höhe für Geburtstagslisten: 4 Zeilen sichtbar, darüber Scroll */
.birthday-card .card-body {
  height: 160px; /* ca. 4 Einträge */
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px; /* Platz für Scrollbar */
}

.absent-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.absent-list .actions {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
}

/* Filterleiste in Card-Headern */
.card-header .h2.clickable {
  cursor: pointer;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.filter-bar .actions {
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 4px;
  margin-top: 0;
}

.filter-input {
  height: 30px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  flex: 1 1 auto;
  min-width: 0;
}

.filter-input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

/* Kompakte Icon-Buttons */
.btn.icon-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  display: grid;
  place-items: center;
}

.btn.icon-btn i {
  font-size: 16px;
  color: var(--text);
}

/* Header: Titel + Icon-Action nebeneinander */
.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.card-header .btn.icon-btn {
  width: 30px;
  height: 30px;
}

/* „Noch in der Schule“: Name links, Aktion rechts */
.present-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.present-list .actions {
  display: flex;
  gap: 8px;
}

/* Leichter Overlay-Dialog */
.overlay-dialog {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
}

.overlay-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .35);
}

.dialog-card {
  position: relative;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, .2);
  width: 92%;
  max-width: 420px;
}

.dialog-header {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
}

.dialog-body {
  padding: 14px;
}

.dialog-actions {
  padding: 12px 14px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid var(--border);
}

.btn-warn {
  background: var(--warn);
  color: #fff;
  border: 0;
  padding: 6px 12px;
  border-radius: 8px;
}

.btn-warn .pi {
  color: #fff;
}
</style>
